import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, GraduationCap, Globe, MapPin, Users, Building2, Calendar, Briefcase, Tag } from "lucide-react";
import { getSchoolDetail, type SchoolListItem } from "@/services/schoolsApi";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const API_URL = import.meta.env.VITE_API_URL;

function formatLocation(loc: { line1?: string | null; line2?: string | null; city?: string | null; postal_code?: string | null; geographic_area?: string | null; country?: string | null }) {
  return [loc.line1, loc.line2, [loc.postal_code, loc.city].filter(Boolean).join(" "), loc.geographic_area, loc.country].filter(Boolean).join(", ");
}

export default function SchoolDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [school, setSchool] = useState<SchoolListItem | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    getSchoolDetail(navigate, Number(id))
      .then(setSchool)
      .catch(() => navigate("/schools"))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) return <SidebarProvider><AppSidebar /><SidebarInset><div className="p-8 text-center text-muted-foreground">Chargement...</div></SidebarInset></SidebarProvider>;
  if (!school) return null;

  const staffRange = school.staff_count_range_start
    ? `${school.staff_count_range_start}-${school.staff_count_range_end ?? "?"}`
    : null;

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <h1 className="text-sm font-medium">Ecole : {school.name}</h1>
          </div>
        </header>

    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <Button variant="ghost" size="sm" onClick={() => navigate("/schools")}>
        <ArrowLeft className="h-4 w-4 mr-2" /> Retour
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <img
              src={`${API_URL}/schools/${school.id}/image`}
              alt=""
              className="h-12 w-12 rounded-lg bg-muted object-cover"
              onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
            />
            <div>
              <div className="flex items-center gap-2">
                <GraduationCap className="h-5 w-5" />
                {school.name}
              </div>
              {school.profile_url && (
                <a href={school.profile_url} target="_blank" rel="noopener noreferrer"
                   className="text-sm font-normal text-blue-600 hover:underline flex items-center gap-1 mt-1">
                  <ExternalLink className="h-3.5 w-3.5" /> Voir sur LinkedIn
                </a>
              )}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Links */}
          <div className="flex flex-wrap gap-3">
            {school.profile_url && (
              <a href={school.profile_url} target="_blank" rel="noopener noreferrer"
                 className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                <ExternalLink className="h-3.5 w-3.5" /> LinkedIn
              </a>
            )}
            {school.website_url && (
              <a href={school.website_url} target="_blank" rel="noopener noreferrer"
                 className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                <Globe className="h-3.5 w-3.5" /> Site web
              </a>
            )}
          </div>

          {/* Details */}
          <dl className="grid grid-cols-2 gap-4 text-sm">
            {school.location && (
              <div>
                <dt className="text-muted-foreground flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> Localisation</dt>
                <dd className="font-medium">{school.location}</dd>
              </div>
            )}
            {school.follower_count != null && school.follower_count > 0 && (
              <div>
                <dt className="text-muted-foreground flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Abonnes LinkedIn</dt>
                <dd className="font-medium">{school.follower_count.toLocaleString("fr-FR")}</dd>
              </div>
            )}
            {school.staff_count != null && (
              <div>
                <dt className="text-muted-foreground flex items-center gap-1"><Building2 className="h-3.5 w-3.5" /> Employes</dt>
                <dd className="font-medium">{school.staff_count.toLocaleString("fr-FR")}{staffRange ? ` (fourchette: ${staffRange})` : ""}</dd>
              </div>
            )}
            {school.founded_year && (
              <div>
                <dt className="text-muted-foreground flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> Fondee en</dt>
                <dd className="font-medium">{school.founded_year}</dd>
              </div>
            )}
            <div>
              <dt className="text-muted-foreground">ID</dt>
              <dd className="font-medium">{school.id}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Ref. externe</dt>
              <dd className="font-medium font-mono text-xs">{school.external_ref}</dd>
            </div>
            {school.created && (
              <div>
                <dt className="text-muted-foreground">Cree le</dt>
                <dd className="font-medium">{new Date(school.created).toLocaleDateString("fr-FR")}</dd>
              </div>
            )}
            {school.updated && (
              <div>
                <dt className="text-muted-foreground">Mis a jour</dt>
                <dd className="font-medium">{new Date(school.updated).toLocaleDateString("fr-FR")}</dd>
              </div>
            )}
          </dl>

          {/* Industries */}
          {school.industries && school.industries.length > 0 && (
            <div>
              <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" /> Industries</p>
              <div className="flex flex-wrap gap-2">
                {school.industries.map((ind) => (
                  <span key={ind.id} className="px-2 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-medium">{ind.name}</span>
                ))}
              </div>
            </div>
          )}

          {/* Specialties */}
          {school.specialties && school.specialties.length > 0 && (
            <div>
              <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1"><Tag className="h-3.5 w-3.5" /> Specialites</p>
              <div className="flex flex-wrap gap-2">
                {school.specialties.map((s, i) => (
                  <span key={i} className="px-2 py-1 bg-gray-100 text-gray-700 rounded-md text-xs font-medium">{s}</span>
                ))}
              </div>
            </div>
          )}

          {/* Locations */}
          {school.locations && school.locations.length > 0 && (
            <div>
              <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> Adresses</p>
              <ul className="space-y-2">
                {school.locations.map((loc, i) => (
                  <li key={i} className="text-sm">
                    {formatLocation(loc)}
                    {loc.is_headquarters && <span className="ml-2 text-xs text-green-700 bg-green-50 px-1.5 py-0.5 rounded">Siege</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Description */}
          {school.description && (
            <div>
              <p className="text-sm text-muted-foreground mb-2">Description</p>
              <p className="text-sm whitespace-pre-line">{school.description}</p>
            </div>
          )}

          {/* Funding */}
          {(school.num_funding_rounds || (school.funding_rounds && school.funding_rounds.length > 0)) && (
            <div>
              <p className="text-sm text-muted-foreground mb-2 font-medium">Financement</p>
              {school.num_funding_rounds && (
                <p className="text-sm mb-2">{school.num_funding_rounds} tour(s) de financement
                  {school.crunchbase_url && (
                    <a href={school.crunchbase_url} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-600 hover:underline text-xs">Crunchbase</a>
                  )}
                </p>
              )}
              {school.funding_rounds?.map((fr) => (
                <div key={fr.id} className="border rounded p-3 mb-2 text-sm">
                  <div className="flex items-center gap-2 flex-wrap">
                    {fr.funding_type && <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded text-xs font-medium">{fr.funding_type.replace(/_/g, " ")}</span>}
                    {fr.money_raised_amount && (
                      <span className="font-medium">
                        {new Intl.NumberFormat("en-US", { style: "currency", currency: fr.money_raised_currency || "USD", maximumFractionDigits: 0 }).format(fr.money_raised_amount)}
                      </span>
                    )}
                    {fr.announced_on && <span className="text-muted-foreground">{fr.announced_on}</span>}
                  </div>
                  {fr.investors.length > 0 && (
                    <div className="mt-1 text-xs text-muted-foreground">
                      Investisseurs : {fr.investors.map((inv, i) => (
                        <span key={i}>
                          {inv.crunchbase_url ? <a href={inv.crunchbase_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{inv.name}</a> : inv.name}
                          {i < fr.investors.length - 1 && ", "}
                        </span>
                      ))}
                      {fr.num_other_investors ? ` +${fr.num_other_investors} autre(s)` : ""}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
